import React, { useState, useEffect, useRef } from 'react';
import { GlassCard } from '../../_shared/GlassCard';
import { Eye, Users, TrendingUp } from 'lucide-react';

const useCounter = (end, duration = 1400, decimals = 0) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    const start = performance.now();
                    const animate = (now) => {
                        const progress = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - progress, 3);
                        const factor = Math.pow(10, decimals);
                        setCount(Math.floor(end * eased * factor) / factor);
                        if (progress < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration, decimals]);

    return [count, ref];
};

const KPICard = ({ icon: Icon, iconColor, label, value, suffix, decimals = 0, subtext }) => {
    const [animatedValue, ref] = useCounter(value, 1600, decimals);
    const display = decimals > 0 ? animatedValue.toFixed(decimals) : Math.floor(animatedValue).toLocaleString();
    return (
        <GlassCard className="flex flex-col justify-between min-h-[200px]">
            <div ref={ref}>
                <div className={`${iconColor} p-3 rounded-xl mb-4 inline-flex`}>
                    <Icon size={22} />
                </div>
                <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-2">{label}</p>
                <p className="text-5xl font-black text-white tracking-tight">
                    {display}{suffix}
                </p>
                {subtext && <p className="text-white/50 text-sm mt-3">{subtext}</p>}
            </div>
        </GlassCard>
    );
};

export const HeroSection = () => {
    return (
        <section className="min-h-[90vh] flex flex-col justify-center items-center text-center px-4 pt-24 pb-12 relative z-10">
            <div className="space-y-6 max-w-5xl mx-auto w-full">

                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/10 backdrop-blur-md mb-2">
                    <span className="text-sm font-medium text-yellow-300 tracking-wide uppercase">Mar – Apr 2026 · 60 Days</span>
                </div>

                <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-yellow-100 to-white drop-shadow-lg leading-tight">
                    Kush 60-Day<br />Performance Report
                </h1>

                <p className="text-lg md:text-xl text-yellow-200/70 max-w-2xl mx-auto leading-relaxed">
                    A combined view of <span className="text-white font-semibold">YouTube + Instagram</span> across two months — and how the audience grew across both platforms in parallel.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-12 w-full">
                    <KPICard
                        icon={Eye}
                        iconColor="bg-yellow-500/20 text-yellow-300"
                        label="Combined Views (YT + IG)"
                        value={2.43}
                        suffix="M"
                        decimals={2}
                        subtext="1.37M YouTube + 1.06M Instagram"
                    />
                    <KPICard
                        icon={Users}
                        iconColor="bg-emerald-500/20 text-emerald-300"
                        label="Total Distribution"
                        value={32247}
                        suffix="+"
                        subtext="22.2K IG followers + 10K YT subscribers"
                    />
                    <KPICard
                        icon={TrendingUp}
                        iconColor="bg-purple-500/20 text-purple-300"
                        label="60-Day Growth"
                        value={7166}
                        subtext="+4,766 IG followers · +2,400 YT subs"
                    />
                </div>

                <div className="mt-10 max-w-3xl mx-auto">
                    <p className="text-white/60 text-base leading-relaxed">
                        YouTube has now crossed <span className="text-white font-semibold">10K subscribers</span> — making it a serious second distribution surface alongside Instagram. The 60-day window saw the audience compound across both platforms simultaneously, with YouTube quietly emerging as the more consistent driver.
                    </p>
                </div>
            </div>
        </section>
    );
};
